<template>
  <v-container>
    <div class="flex items-center gap-x-2">
      <v-btn text @click="$router.go(-1)">
        <v-icon left> mdi-arrow-left-thick </v-icon>
      </v-btn>
      <p class="text-2xl mb-0">Send SnappyGift</p>
    </div>
    <div v-if="step === 0" class="max-w-md mt-10 text-center">
      <h1 class="text-2xl font-bold mb-4">
        Send money to your friends with SnappyGift
      </h1>
      <p class="text-gray-500 mb-4">
        To send money to a friend on SnappyExchange using SnappyGift, simply
        enter your friend's SnappyExchange Username and specify the amount you'd
        like to transfer.
      </p>
      <v-btn color="primary" @click="step++"> Continue </v-btn>
    </div>
    <div v-if="step === 1" class="max-w-md mt-10 text-center">
      <div class="mb-4">
        <div
          class="flex items-center gap-x-2 bg-gray-100 p-2 rounded-lg border border-gray-200 border-solid w-full"
        >
          <input
            type="text"
            placeholder="Snappy Username"
            class="bg-transparent outline-none border-none w-full"
            v-model="friendUsername"
            :disabled="loadingRecipients"
          />
          <button
            class="bg-accent text-black px-4 py-2 rounded-lg"
            @click="recipientSearch"
            :disabled="loadingRecipients"
          >
            {{ loadingRecipients ? 'Searching...' : 'Verify' }}
          </button>
        </div>
      </div>

      <div
        class="mb-4 flex items-start text-left gap-x-2 text-gray-500 text-sm bg-yellow-100 p-2 rounded-lg"
      >
        <span><v-icon>mdi-information</v-icon></span>
        Double check the username you enter as sending money to the wrong
        username is irreversible.
      </div>

      <div
        v-if="recipient"
        class="mb-4 bg-white shadow-md p-4 rounded-lg text-left"
      >
        <div class="text-gray-500 text-sm">Friend Details</div>
        <div class="text-gray-800 text-sm">
          {{ recipient.fname }} {{ recipient.lname }}
        </div>
        <div class="text-gray-500 text-sm">{{ recipient.username }}</div>
      </div>

      <div v-if="recipient" class="mb-4">
        <input
          class="w-full p-2 rounded-lg border border-gray-200 border-solid focus:outline-none focus:ring-2 focus:ring-primary"
          type="number"
          placeholder="Amount"
          v-model="amount"
        />
      </div>

      <div v-if="recipient">
        <v-btn block color="primary" @click="step++">Continue</v-btn>
      </div>
    </div>
    <div v-if="step === 2" class="max-w-md mt-10 text-center">
      <div class="mb-4 bg-white shadow-md p-4 rounded-lg text-left">
        <div class="text-gray-500 text-lg">SnappyGift</div>
        <div class="text-gray-800 text-lg">
          N {{ numberWithCommas(amount) }}
        </div>
      </div>

      <div class="mb-4 bg-white shadow-md p-4 rounded-lg text-left">
        <div class="flex items-center justify-between">
          <div class="text-gray-500 text-sm">SnappyGift to</div>
          <div class="text-gray-800 text-base">{{ recipient.username }}</div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-gray-500 text-sm">Friend's Name</div>
          <div class="text-gray-800 text-base">
            {{ recipient.fname }} {{ recipient.lname }}
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-gray-500 text-sm">Amount to send</div>
          <div class="text-gray-800 text-base">
            {{ userCurrency }} {{ numberWithCommas(amount) }}
          </div>
        </div>
      </div>

      <div>
        <v-btn
          block
          color="primary"
          :loading="sendingOtp"
          :disabled="sendingOtp"
          @click="
            () => {
              generateOTP()
            }
          "
          >{{ sendingOtp ? 'Sending OTP...' : 'Proceed' }}</v-btn
        >
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="600">
      <v-card v-if="dialogStep === 'email-verification'">
        <v-card-title> Complete your payment </v-card-title>
        <v-card-text>
          <p>We've sent a 6-digit authentication to: {{ user.email }}</p>
          <p>Enter the code to complete your payment</p>
          <v-text-field
            label="Authentication Code"
            type="number"
            v-model="otp"
          ></v-text-field>
          <div>
            <v-btn
              block
              color="primary"
              @click="makeTransfer"
              :disabled="sendingOtp || loadingTransfer"
              :loading="loadingTransfer"
              >Complete</v-btn
            >
            <v-btn
              text
              color="primary"
              @click="generateOTP"
              :loading="sendingOtp"
              :disabled="sendingOtp"
            >
              {{ sendingOtp ? 'Sending OTP...' : 'Resend OTP' }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
      <v-card v-if="dialogStep === 'successful-payment'">
        <v-card-title> SnappyGift Sent Successfully </v-card-title>
        <v-card-text>
          <p>Your SnappyGift has been sent successfully</p>
          <p>
            You've successfully sent N {{ numberWithCommas(amount) }} to
            {{ recipient.username }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn block color="primary" @click="closeDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex/dist/vuex.common.js'
import misc from '../../../api/users/misc'
import transfer from '../../../api/users/transfer'
import { numberWithCommas } from '../../../utils/helpers'

export default {
  data() {
    return {
      numberWithCommas,
      step: 0,
      // email-verification, successful-payment
      dialogStep: 'email-verification',
      friendUsername: '',
      amount: '',
      dialog: false,
      loadingRecipients: false,
      hasSearched: false,
      recipient: null,
      sendingOtp: false,
      otp: '',
      loadingTransfer: false
    }
  },
  computed: {
    ...mapState({
      user: ({ user }) => user.user
    })
  },
  created() {},
  methods: {
    closeDialog() {
      this.dialog = false
      this.dialogStep = 'email-verification'
      this.step = 0
      this.otp = ''
      this.sendingOtp = false
      this.loadingTransfer = false
      this.friendUsername = ''
      this.amount = ''
      this.recipient = null
    },
    async recipientSearch() {
      this.loadingRecipients = true
      const res = await misc.misc().recipientSearch(this.friendUsername)
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.message,
          error: true,
          status: true
        })
        return
      }
      this.hasSearched = true
      this.recipient = res.data.data
      this.loadingRecipients = false
    },
    async generateOTP() {
      this.sendingOtp = true
      const res = await misc.misc().generateOtp('transfer')
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.message,
          error: true,
          status: true
        })
        return
      }

      this.$store.dispatch('alert', {
        message: 'OTP sent successfully, kindly check your email',
        status: true
      })
      this.sendingOtp = false
      this.dialogStep = 'email-verification'
      this.dialog = true
    },
    async makeTransfer() {
      this.loadingTransfer = true

      const payload = {
        amount: this.amount,
        sendDetails: this.recipient.username,
        otp: this.otp
      }

      const res = await transfer.data().makeTransfer(payload)
      if (res.error) {
        this.$store.dispatch('alert', {
          message:
            res.errorMessage.message ||
            `${res.internalError.message}, please try again`,
          error: true,
          status: true
        })
        this.loadingTransfer = false
        return
      }
      this.$store.dispatch('alert', {
        message: 'Transfer request sent successfully',
        status: true
      })
      this.loadingTransfer = false
      this.otp = ''
      this.dialogStep = 'successful-payment'
      this.dialog = true
      this.step = 0
    }
  }
}
</script>

<style></style>
